import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Fokus für ein Fenster über der Seite: beim Öffnen auf das erste Feld (sonst auf das Fenster
 * selbst, das dafür `tabIndex={-1}` trägt), Tab bleibt im Fenster, Escape schließt, und beim
 * Schließen geht der Fokus zurück auf den Knopf, der es geöffnet hat.
 *
 * Der Ref gehört an das Element mit `role="dialog"` oder dessen Inhalt.
 */
export function useDialogFocus<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(i => i.offsetParent !== null || i === document.activeElement);
      if (items.length === 0) { e.preventDefault(); el.focus(); return; }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === firstItem || !el.contains(active))) { e.preventDefault(); lastItem.focus(); }
      else if (!e.shiftKey && active === lastItem) { e.preventDefault(); firstItem.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [onClose]);
  return ref;
}
